import {useCallback, useEffect, useRef, useState} from 'react';
import {setUnsavedGuard, clearUnsavedGuard} from './unsavedChangesGuard';

const useUnsavedChanges = ({hasUnsavedChanges, onSave, onDiscard, navigation}) => {
  const [promptVisible, setPromptVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const pendingNavigation = useRef(null);
  const dirtyRef = useRef(hasUnsavedChanges);

  dirtyRef.current = hasUnsavedChanges;

  const blockNavigation = useCallback(performNavigation => {
    pendingNavigation.current = performNavigation;
    setPromptVisible(true);
  }, []);

  const resumeNavigation = useCallback(() => {
    const performNavigation = pendingNavigation.current;
    pendingNavigation.current = null;
    clearUnsavedGuard(blockNavigation);
    if (performNavigation) {
      performNavigation();
    }
  }, [blockNavigation]);

  useEffect(() => {
    const handleBeforeUnload = event => {
      if (!dirtyRef.current) return undefined;
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    if (hasUnsavedChanges) {
      setUnsavedGuard(blockNavigation);
      if (typeof window !== 'undefined') {
        window.addEventListener('beforeunload', handleBeforeUnload);
      }
    } else {
      clearUnsavedGuard(blockNavigation);
    }

    return () => {
      clearUnsavedGuard(blockNavigation);
      if (typeof window !== 'undefined') {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    };
  }, [hasUnsavedChanges, blockNavigation]);

  useEffect(() => {
    if (!navigation || typeof navigation.addListener !== 'function') return undefined;

    const unsubscribe = navigation.addListener('beforeRemove', event => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      pendingNavigation.current = () => navigation.dispatch(event.data.action);
      setPromptVisible(true);
    });

    return unsubscribe;
  }, [navigation]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await onSave();
      setPromptVisible(false);
      resumeNavigation();
    } finally {
      setSaving(false);
    }
  }, [onSave, resumeNavigation]);

  const handleDiscard = useCallback(() => {
    if (onDiscard) {
      onDiscard();
    }
    setPromptVisible(false);
    resumeNavigation();
  }, [onDiscard, resumeNavigation]);

  const handleStay = useCallback(() => {
    pendingNavigation.current = null;
    setPromptVisible(false);
  }, []);

  return {promptVisible, saving, handleSave, handleDiscard, handleStay};
};

export default useUnsavedChanges;
